import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  StyleSheet,
  TextInput,
  Modal,
} from '../components/rn';
import ActivityIndicator from '../components/LoadingIndicator';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { spacing, fontSize, ThemeColors, radius } from '../theme';
import { useTheme } from '../theme/ThemeContext';
import CompactActionButton from '../components/CompactActionButton';
import { useTranslation } from '../i18n/I18nContext';
import { api, NetworkError } from '../services/api';
import { formatMoney } from '../data/currencies';
import Avatar from '../components/Avatar';
import VerifiedBadge from '../components/VerifiedBadge';
import { ISCO_MAJOR_CODES } from '../data/isco';
import NotificationBellButton from '../components/NotificationBellButton';
import GlassControlButton from '../components/GlassControlButton';
import Ionicons from '@expo/vector-icons/Ionicons';
import DiscoverMapScreen from './DiscoverMapScreen';
import { floatingDockContentInset, floatingDockScrollInset } from '../components/floatingDockLayout';

type User = {
  /** 9.26 / D-641 — resmî hesap (mavi işaret). */
  is_official?: boolean;
  id: number;
  name: string;
  photo_url: string | null;
  university: string | null;
  profession: string | null;
  price_message: number;
  currency: string;
};

type Props = {
  /** 1.3.7 / D-665 — keşif isteği oturumla gider; engel süzgeci ancak öyle çalışır. */
  token?: string | null;
  myId?: number | null;
  onSelectUser: (userId: number) => void;
  onWallet: () => void; // artık kullanılmıyor, dock'a taşındı
  onNotificationCenter: () => void;
};

export default function DiscoverScreen({ token, myId, onSelectUser, onNotificationCenter }: Props) {
  const insets = useSafeAreaInsets();
  const colors = useTheme();
  const { t, lang } = useTranslation();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [query, setQuery] = useState('');
  const [group, setGroup] = useState('');
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [retry, setRetry] = useState(0);
  const [mapOpen,setMapOpen]=useState(false);
  const [mapPresented,setMapPresented]=useState(false);
  const closeMap=()=>{setMapOpen(false);setMapPresented(false);};
  const listRef = useRef<FlatList<User>>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setFailed(false);
    setUsers([]);
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    // Only this side-effect-free read gets one bounded transport retry. Never
    // replay authentication failures, invalid responses or message mutations.
    const load = async (retried = false) => {
      try {
        const rows = await api.discover(query.trim() || undefined, lang, token, group || undefined);
        if (!active) return;
        setUsers(rows);
      } catch (error) {
        if (!active) return;
        if (!retried && error instanceof NetworkError) {
          retryTimer = setTimeout(() => { load(true); }, 600);
          return;
        }
        setFailed(true);
      }
      if (active) setLoading(false);
    };
    const timer = setTimeout(() => { load(); }, query.trim() ? 300 : 0);
    return () => { clearTimeout(timer); clearTimeout(retryTimer); active = false; };
  }, [query, group, lang, token, retry]);

  const categoryLabel = group ? t(`isco.${group}`) : t('discover.all');

  return (
    <View style={[styles.container, { paddingTop: insets.top }]}>
      <View style={styles.headerRow}>
        <View style={styles.title}><GlassControlButton testID="discover-open-map" onPress={()=>setMapOpen(true)} accessibilityLabel={`${t('discover.title')}: ${t('map.title')}`}>
          <Ionicons name="map-outline" size={22} color={colors.black}/>
        </GlassControlButton></View>
        <NotificationBellButton onPress={onNotificationCenter} />
      </View>

      <Modal visible={mapOpen&&!!token&&!!myId} animationType="slide" onShow={()=>setMapPresented(true)} onRequestClose={closeMap}>
        {mapOpen&&mapPresented&&token&&myId?<DiscoverMapScreen key={`${myId}:${token}`} token={token} myId={myId} onClose={closeMap}
          onOpenProfile={userId=>{closeMap();onSelectUser(userId);}}/>:null}
      </Modal>

      <TextInput
        style={styles.searchInput}
        placeholder={t('common.search')}
        placeholderTextColor={colors.gray400}
        value={query}
        onChangeText={setQuery}
        accessibilityLabel={t('common.search')}
        clearButtonMode="while-editing"
      />

      <TouchableOpacity style={styles.categoryToggle} testID="discover-categories"
        accessibilityRole="button" accessibilityState={{ expanded: categoriesOpen }}
        onPress={() => { setCategoriesOpen(open => !open); listRef.current?.scrollToOffset({ offset: 0, animated: false }); }}>
        <View style={styles.info}>
          <Text style={styles.categoryCaption}>{t('discover.categories')}</Text>
          <Text style={styles.categoryValue}>{categoryLabel}</Text>
        </View>
        <Text style={styles.categoryValue} accessibilityElementsHidden>{categoriesOpen ? '−' : '+'}</Text>
      </TouchableOpacity>

      <FlatList
        ref={listRef}
        data={users}
        keyboardShouldPersistTaps="handled"
        keyExtractor={(item) => String(item.id)}
        contentContainerStyle={[styles.list, { paddingBottom: insets.bottom + floatingDockContentInset }]}
        scrollIndicatorInsets={{ bottom: insets.bottom + floatingDockScrollInset }}
        ListHeaderComponent={categoriesOpen ? <View style={styles.categorySection}>
          <Text style={styles.categoryHint}>{t('discover.categoryHint')}</Text>
          <View style={styles.categories}>
            {['', ...ISCO_MAJOR_CODES].map(code => <TouchableOpacity key={code || 'all'}
              testID={`discover-group-${code || 'all'}`} accessibilityRole="button"
              accessibilityState={{ selected: group === code }}
              style={[styles.category, group === code && styles.categorySelected]}
              onPress={() => { setGroup(code); setCategoriesOpen(false); }}>
              <Text style={[styles.categoryText, group === code && styles.categoryTextSelected]}>
                {code ? t(`isco.${code}`) : t('discover.all')}
              </Text>
            </TouchableOpacity>)}
          </View>
        </View> : null}
        renderItem={({ item }) => (
          <TouchableOpacity style={styles.row} onPress={() => onSelectUser(item.id)}>
            <Avatar photoUrl={item.photo_url} name={item.name} size={52} style={styles.avatar} />
            <View style={styles.info}>
              <View style={styles.nameRow}>
                <View style={styles.nameWithBadge}>
                  <Text style={styles.name} numberOfLines={1}>{item.name}</Text>
                  {item.is_official && <VerifiedBadge size={15} />}
                </View>
                <Text style={styles.price} numberOfLines={1}>
                  {Number(item.price_message) === 0 ? t('discover.free') : formatMoney(item.price_message, item.currency, lang)}
                </Text>
              </View>
              {(item.profession || item.university) && (
                <Text style={styles.university} numberOfLines={1}>
                  {[item.profession, item.university].filter(Boolean).join(' · ')}
                </Text>
              )}
            </View>
          </TouchableOpacity>
        )}
        ItemSeparatorComponent={() => <View style={styles.separator} />}
        ListEmptyComponent={loading ? <View style={styles.feedback}><ActivityIndicator color={colors.black} /></View>
          : failed ? <View style={styles.feedback}>
            <Text style={styles.errorText}>{t('discover.failed')}</Text>
            <CompactActionButton variant="primary" label={t('common.retry')}
              onPress={() => setRetry(value => value + 1)} testID="discover-retry" />
          </View> : <View style={styles.feedback}>
            <Text style={styles.empty}>{t(query.trim() || group ? 'inbox.searchEmpty' : 'discover.empty')}</Text>
            {!!(query.trim() || group) && <CompactActionButton variant="plain" label={t('discover.reset')}
              onPress={() => { setQuery(''); setGroup(''); }} testID="discover-reset" />}
          </View>}
      />
    </View>
  );
}

const makeStyles = (colors: ThemeColors) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.white,
  },
  title: { flex: 1, fontSize: fontSize.lg, fontWeight: '600', color: colors.black },
  feedback: { alignItems: 'center', paddingVertical: spacing.xl, gap: spacing.md },
  categoryToggle: { flexDirection: 'row', alignItems: 'center', gap: spacing.md,
    marginHorizontal: spacing.lg, marginBottom: spacing.sm, padding: spacing.md,
    borderWidth: 1, borderColor: colors.gray200, borderRadius: radius.card, minHeight: 60 },
  categoryCaption: { fontSize: fontSize.sm, color: colors.gray600, marginBottom: spacing.xs },
  categoryValue: { fontSize: fontSize.md, color: colors.black, fontWeight: '600' },
  categorySection: { paddingVertical: spacing.sm },
  categoryHint: { fontSize: fontSize.sm, color: colors.gray600, marginBottom: spacing.md },
  categories: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  category: { maxWidth: '100%', minHeight: 44, justifyContent: 'center', paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm, borderRadius: radius.control, backgroundColor: colors.gray100 },
  categorySelected: { backgroundColor: colors.black },
  categoryText: { fontSize: fontSize.sm, color: colors.gray600, flexShrink: 1 },
  categoryTextSelected: { color: colors.white, fontWeight: '600' },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
  },
  errorText: { fontSize: fontSize.md, color: colors.gray400, marginBottom: spacing.md },
  searchInput: {
    marginHorizontal: spacing.lg,
    marginBottom: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    backgroundColor: colors.gray100,
    borderRadius: radius.control,
    fontSize: fontSize.md,
    color: colors.black,
  },
  empty: {
    fontSize: fontSize.md,
    color: colors.gray400,
    textAlign: 'center',
  },
  list: {
    paddingHorizontal: spacing.lg,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.md,
  },
  avatar: {
    width: 52,
    height: 52,
    borderRadius: 26,
    marginRight: spacing.md,
  },
  info: {
    flex: 1,
    minWidth: 0,
  },
  nameRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  // 9.26 / D-641 — ad uzunsa kısalan o, rozet değil. Fiyattan uzaklık grupta;
  // ad kendi içinde BÜYÜMÜYOR: `flex: 1` ilk sürümde adı satırın sonuna kadar
  // uzatıp işareti fiyatın dibine itmişti.
  nameWithBadge: {
    flexDirection: 'row', alignItems: 'center', flexShrink: 1, minWidth: 0, marginRight: spacing.sm,
  },
  name: {
    flexShrink: 1,
    minWidth: 0,
    fontSize: fontSize.md,
    fontWeight: '600',
    color: colors.black,
  },
  price: {
    flexShrink: 0,
    fontSize: fontSize.sm,
    color: colors.gray400,
  },
  university: {
    fontSize: fontSize.sm,
    color: colors.gray400,
    marginTop: 2,
  },
  separator: {
    height: 1,
    backgroundColor: colors.gray100,
  },
});
